/**
 * Lightweight incremental markdown-to-DOM renderer for streaming agent responses.
 * Replaces the `streaming-markdown` npm package (~12KB min) with ~150 lines.
 *
 * Supports the subset of markdown that AI agents actually produce:
 * - Paragraphs, line breaks
 * - Bold (**), italic (*), strikethrough (~~), inline code (`)
 * - Code blocks (``` with optional language)
 * - Headings (#..####)
 * - Unordered/ordered lists
 * - Blockquotes (>)
 * - Links [text](url)
 * - Horizontal rules (---)
 *
 * This renderer is ephemeral — the streaming DOM is replaced by the entity-based
 * final render once the full message arrives from teamgram with entities.
 * Visual fidelity is good-enough, not pixel-perfect.
 */

export interface StreamParser {
  /** Append a chunk of markdown text (incremental). */
  write(chunk: string): void;
  /** Flush remaining state and close the parser. */
  end(): void;
}

const BLOCK_RE = /^(#{1,4})\s|^>\s?|^```|^---+\s*$|^(\s*)([-*]|\d+\.)\s/;

export function createStreamParser(root: HTMLElement): StreamParser {
  let buffer = '';
  let currentBlock: HTMLElement | undefined;
  let inCodeFence = false;
  let codeLang = '';
  let codeContent = '';
  let ended = false;

  function flushLine(line: string) {
    if (inCodeFence) {
      if (line.match(/^```\s*$/)) {
        inCodeFence = false;
        // If code content is a table (all non-empty lines start with |), render as table
        const codeLines = codeContent.split('\n').filter((l) => l.trim());
        const isTable = codeLines.length > 0 && codeLines.every((l) => l.trimStart().startsWith('|'));
        if (isTable) {
          // Render as table — feed each line through the table handler
          codeLines.forEach((tl) => flushLine(tl));
          closeParagraph();
        } else {
          const pre = document.createElement('pre');
          const code = document.createElement('code');
          if (codeLang) code.className = `language-${codeLang}`;
          code.textContent = codeContent;
          pre.appendChild(code);
          root.appendChild(pre);
        }
        codeContent = '';
        codeLang = '';
        currentBlock = undefined;
        return;
      }
      codeContent += (codeContent ? '\n' : '') + line;
      return;
    }

    // Code fence open
    const fenceMatch = line.match(/^```(\w*)\s*$/);
    if (fenceMatch) {
      closeParagraph();
      inCodeFence = true;
      codeLang = fenceMatch[1] || '';
      codeContent = '';
      return;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      closeParagraph();
      root.appendChild(document.createElement('hr'));
      return;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      closeParagraph();
      const level = Math.min(headingMatch[1].length, 4);
      const h = document.createElement(`h${level}`);
      appendInline(h, headingMatch[2]);
      root.appendChild(h);
      return;
    }

    // Blockquote
    const bqMatch = line.match(/^>\s?(.*)$/);
    if (bqMatch) {
      closeParagraph();
      const bq = document.createElement('blockquote');
      appendInline(bq, bqMatch[1]);
      root.appendChild(bq);
      return;
    }

    // Table row: | col | col |
    if (line.trimStart().startsWith('|')) {
      const trimmed = line.trim();
      // Skip separator rows like |---|---|
      if (/^\|[\s:]*-+[\s:]*\|/.test(trimmed)) return;

      const cells = trimmed.split('|').filter(Boolean).map((c) => c.trim());
      if (cells.length > 0) {
        if (!currentBlock || currentBlock.tagName !== 'TABLE') {
          closeParagraph();
          currentBlock = document.createElement('table');
          root.appendChild(currentBlock);
        }
        const tr = document.createElement('tr');
        const isHeader = !currentBlock.querySelector('tr');
        cells.forEach((cell) => {
          const td = document.createElement(isHeader ? 'th' : 'td');
          appendInline(td, cell);
          tr.appendChild(td);
        });
        currentBlock.appendChild(tr);
        return;
      }
    }

    // Table row continuation: building a table and line has pipes but doesn't start with |
    if (currentBlock?.tagName === 'TABLE' && !line.trimStart().startsWith('|')
      && line.includes('|') && line.trim().endsWith('|')) {
      const lastTr = currentBlock.lastElementChild as HTMLElement;
      if (lastTr) {
        const cells = line.trim().split('|').filter(Boolean).map((c) => c.trim());
        const isHeader = lastTr.children[0]?.tagName === 'TH';
        cells.forEach((cell) => {
          const td = document.createElement(isHeader ? 'th' : 'td');
          appendInline(td, cell);
          lastTr.appendChild(td);
        });
        return;
      }
    }

    // Unordered list item
    const ulMatch = line.match(/^(\s*)[-*]\s+(.+)$/);
    if (ulMatch) {
      ensureList('ul');
      const li = document.createElement('li');
      appendInline(li, ulMatch[2]);
      currentBlock!.appendChild(li);
      return;
    }

    // Ordered list item
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      ensureList('ol');
      const li = document.createElement('li');
      appendInline(li, olMatch[2]);
      currentBlock!.appendChild(li);
      return;
    }

    // Blank line — close current paragraph
    if (line.trim() === '') {
      closeParagraph();
      return;
    }

    // Regular text — append to paragraph
    if (!currentBlock || currentBlock.tagName !== 'P') {
      closeParagraph();
      currentBlock = document.createElement('p');
      root.appendChild(currentBlock);
    } else {
      currentBlock.appendChild(document.createElement('br'));
    }
    appendInline(currentBlock, line);
  }

  function ensureList(tag: 'ul' | 'ol') {
    if (currentBlock && currentBlock.tagName === tag.toUpperCase()) return;
    closeParagraph();
    currentBlock = document.createElement(tag);
    root.appendChild(currentBlock);
  }

  function closeParagraph() {
    currentBlock = undefined;
  }

  /** Parse inline markdown and append to parent element. */
  function appendInline(parent: HTMLElement, text: string) {
    // Process inline patterns: **bold**, *italic*, ~~strike~~, `code`, [text](url)
    let i = 0;
    let plain = '';

    const flush = () => {
      if (plain) {
        parent.appendChild(document.createTextNode(plain));
        plain = '';
      }
    };

    while (i < text.length) {
      // Bold: **text**
      if (text[i] === '*' && text[i + 1] === '*') {
        const close = text.indexOf('**', i + 2);
        if (close > i + 2) {
          flush();
          const strong = document.createElement('strong');
          strong.textContent = text.slice(i + 2, close);
          parent.appendChild(strong);
          i = close + 2;
          continue;
        }
      }

      // Strikethrough: ~~text~~
      if (text[i] === '~' && text[i + 1] === '~') {
        const close = text.indexOf('~~', i + 2);
        if (close > i + 2) {
          flush();
          const del = document.createElement('del');
          del.textContent = text.slice(i + 2, close);
          parent.appendChild(del);
          i = close + 2;
          continue;
        }
      }

      // Inline code: `text`
      if (text[i] === '`' && text[i + 1] !== '`') {
        const close = text.indexOf('`', i + 1);
        if (close > i + 1) {
          flush();
          const code = document.createElement('code');
          code.textContent = text.slice(i + 1, close);
          parent.appendChild(code);
          i = close + 1;
          continue;
        }
      }

      // Link: [text](url)
      if (text[i] === '[') {
        const closeBracket = text.indexOf(']', i + 1);
        if (closeBracket > i + 1 && text[closeBracket + 1] === '(') {
          const closeParen = text.indexOf(')', closeBracket + 2);
          if (closeParen > closeBracket + 2) {
            flush();
            const a = document.createElement('a');
            a.href = text.slice(closeBracket + 2, closeParen);
            a.textContent = text.slice(i + 1, closeBracket);
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            parent.appendChild(a);
            i = closeParen + 1;
            continue;
          }
        }
      }

      // Italic: *text* (single, after bold check)
      if (text[i] === '*' && text[i + 1] !== '*') {
        const close = text.indexOf('*', i + 1);
        if (close > i + 1 && text[close + 1] !== '*') {
          const inner = text.slice(i + 1, close);
          if (inner.trim() && inner[0] !== ' ' && inner[inner.length - 1] !== ' ') {
            flush();
            const em = document.createElement('em');
            em.textContent = inner;
            parent.appendChild(em);
            i = close + 1;
            continue;
          }
        }
      }

      plain += text[i];
      i++;
    }
    flush();
  }

  function processBuffer() {
    // Process complete lines from buffer
    let nlIdx = buffer.indexOf('\n');
    while (nlIdx >= 0) {
      const line = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 1);
      flushLine(line);
      nlIdx = buffer.indexOf('\n');
    }
  }

  return {
    write(chunk: string) {
      if (ended) return;
      buffer += chunk;
      processBuffer();
    },
    end() {
      if (ended) return;
      ended = true;
      // Flush remaining buffer as final line
      if (buffer) {
        flushLine(buffer);
        buffer = '';
      }
      // Close unclosed code fence
      if (inCodeFence) {
        const pre = document.createElement('pre');
        const code = document.createElement('code');
        if (codeLang) code.className = `language-${codeLang}`;
        code.textContent = codeContent;
        pre.appendChild(code);
        root.appendChild(pre);
        inCodeFence = false;
      }
    },
  };
}
